'use client';

import React, { useState, useEffect } from 'react';
import MenuFilters from './MenuFilters';
import MenuCard from './MenuCard';
import OrderModal from './OrderModal';
import Icon from '@/components/ui/AppIcon';

interface Ingredient {
  name: string;
  icon: string;
}

interface NutritionalInfo {
  calories: number;
  protein: string;
  carbs: string;
  fat: string;
}

interface MenuItem {
  id: number;
  name: string;
  category: string;
  description: string;
  price: number;
  image: string;
  alt: string;
  spiceLevel: string;
  sweetnessLevel: string;
  preparationTime: string;
  availability: 'available' | 'limited' | 'out-of-stock';
  isCustomerFavorite: boolean;
  isSeasonal: boolean;
  ingredients: Ingredient[];
  allergens: string[];
  dietary: string[];
  nutritionalInfo: NutritionalInfo;
  culturalNote: string;
}

const PaanMenuInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState('all');
  const [selectedSweetness, setSelectedSweetness] = useState('all');
  const [selectedDietary, setSelectedDietary] = useState('all');
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const menuItems: MenuItem[] = [
  {
    id: 1,
    name: "Meetha Paan",
    category: "classic",
    description: "Traditional sweet paan with gulkand, coconut, cherries, and aromatic spices. A timeless favorite that melts in your mouth.",
    price: 30,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed753ccc-1768329572553.png",
    alt: "Close-up of traditional meetha paan with visible gulkand rose preserve, shredded coconut, and red cherries on fresh betel leaf",
    spiceLevel: "mild",
    sweetnessLevel: "high",
    preparationTime: "2-3 mins",
    availability: "available",
    isCustomerFavorite: true,
    isSeasonal: false,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Gulkand", icon: "HeartIcon" },
    { name: "Coconut", icon: "StarIcon" },
    { name: "Cherries", icon: "SparklesIcon" },
    { name: "Fennel Seeds", icon: "StarIcon" },
    { name: "Cardamom", icon: "SparklesIcon" }],

    allergens: ["Nuts", "Coconut"],
    dietary: ["Vegetarian"],
    nutritionalInfo: {
      calories: 85,
      protein: "1.2g",
      carbs: "18g",
      fat: "2.5g"
    },
    culturalNote: "Meetha paan has been served after meals in Indian households for centuries as a digestive aid and mouth freshener."
  },
  {
    id: 2,
    name: "Banarasi Paan",
    category: "premium",
    description: "Premium paan from the holy city of Varanasi, featuring special tobacco blend, silver leaf, and exotic spices.",
    price: 80,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_184f89723-1768329574483.png",
    alt: "Luxurious Banarasi paan with visible silver leaf coating, tobacco blend, and premium spices arranged on ornate betel leaf",
    spiceLevel: "medium",
    sweetnessLevel: "medium",
    preparationTime: "4-5 mins",
    availability: "available",
    isCustomerFavorite: true,
    isSeasonal: false,
    ingredients: [
    { name: "Premium Betel Leaf", icon: "SparklesIcon" },
    { name: "Silver Leaf", icon: "StarIcon" },
    { name: "Tobacco", icon: "FireIcon" },
    { name: "Katha", icon: "SparklesIcon" },
    { name: "Lime", icon: "StarIcon" },
    { name: "Supari", icon: "SparklesIcon" }],

    allergens: ["Tobacco", "Nuts"],
    dietary: [],
    nutritionalInfo: {
      calories: 65,
      protein: "0.8g",
      carbs: "12g",
      fat: "1.8g"
    },
    culturalNote: "Banarasi paan is considered the king of all paans, originating from the ancient city of Varanasi with a 400-year tradition."
  },
  {
    id: 3,
    name: "Chocolate Paan",
    category: "special",
    description: "Modern fusion paan with Belgian chocolate, dry fruits, and sweet gulkand. Perfect for chocolate lovers.",
    price: 60,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b3c4d9d0-1768329575403.png",
    alt: "Innovative chocolate paan showing melted Belgian chocolate drizzle over betel leaf with visible cashews and almonds",
    spiceLevel: "mild",
    sweetnessLevel: "high",
    preparationTime: "3-4 mins",
    availability: "available",
    isCustomerFavorite: true,
    isSeasonal: false,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Belgian Chocolate", icon: "HeartIcon" },
    { name: "Cashews", icon: "StarIcon" },
    { name: "Almonds", icon: "SparklesIcon" },
    { name: "Gulkand", icon: "HeartIcon" },
    { name: "Coconut", icon: "StarIcon" }],

    allergens: ["Nuts", "Dairy", "Coconut"],
    dietary: ["Vegetarian"],
    nutritionalInfo: {
      calories: 120,
      protein: "2.5g",
      carbs: "22g",
      fat: "4.2g"
    },
    culturalNote: "A modern innovation that bridges traditional paan culture with contemporary chocolate indulgence."
  },
  {
    id: 4,
    name: "Fire Paan",
    category: "special",
    description: "Spicy and bold paan with extra tobacco, clove oil, and hot spices. For the adventurous palate only.",
    price: 50,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18c0f634f-1768329572363.png",
    alt: "Intense fire paan with visible red chili flakes, clove oil drops, and strong tobacco blend on dark green betel leaf",
    spiceLevel: "hot",
    sweetnessLevel: "low",
    preparationTime: "3-4 mins",
    availability: "available",
    isCustomerFavorite: false,
    isSeasonal: false,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Strong Tobacco", icon: "FireIcon" },
    { name: "Clove Oil", icon: "FireIcon" },
    { name: "Red Chili", icon: "FireIcon" },
    { name: "Black Pepper", icon: "FireIcon" },
    { name: "Menthol", icon: "SparklesIcon" }],

    allergens: ["Tobacco"],
    dietary: [],
    nutritionalInfo: {
      calories: 45,
      protein: "0.5g",
      carbs: "8g",
      fat: "1.2g"
    },
    culturalNote: "Fire paan is a modern creation for those who seek an intense, bold experience with maximum heat."
  },
  {
    id: 5,
    name: "Rajwadi Paan",
    category: "premium",
    description: "Royal paan with saffron, gold leaf, premium dry fruits, and exotic spices. A truly regal experience.",
    price: 120,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19244ae8a-1768329574370.png",
    alt: "Luxurious Rajwadi paan adorned with visible gold leaf, saffron strands, pistachios, and premium rose petals on pristine betel leaf",
    spiceLevel: "mild",
    sweetnessLevel: "medium",
    preparationTime: "5-6 mins",
    availability: "limited",
    isCustomerFavorite: true,
    isSeasonal: false,
    ingredients: [
    { name: "Premium Betel Leaf", icon: "SparklesIcon" },
    { name: "Gold Leaf", icon: "StarIcon" },
    { name: "Saffron", icon: "SparklesIcon" },
    { name: "Pistachios", icon: "StarIcon" },
    { name: "Rose Petals", icon: "HeartIcon" },
    { name: "Gulkand", icon: "HeartIcon" }],

    allergens: ["Nuts"],
    dietary: ["Vegetarian"],
    nutritionalInfo: {
      calories: 95,
      protein: "2.8g",
      carbs: "16g",
      fat: "3.5g"
    },
    culturalNote: "Rajwadi paan was traditionally served in royal courts and palaces, symbolizing luxury and hospitality."
  },
  {
    id: 6,
    name: "Ice Paan",
    category: "special",
    description: "Refreshing cold paan with menthol crystals, mint, and cooling spices. Perfect for hot summer days.",
    price: 45,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed753ccc-1768329572553.png",
    alt: "Refreshing ice paan with visible menthol crystals, fresh mint leaves, and ice-cold ingredients on chilled betel leaf",
    spiceLevel: "mild",
    sweetnessLevel: "medium",
    preparationTime: "2-3 mins",
    availability: "available",
    isCustomerFavorite: false,
    isSeasonal: true,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Menthol Crystals", icon: "SparklesIcon" },
    { name: "Fresh Mint", icon: "StarIcon" },
    { name: "Fennel Seeds", icon: "StarIcon" },
    { name: "Coconut", icon: "StarIcon" },
    { name: "Gulkand", icon: "HeartIcon" }],

    allergens: ["Coconut"],
    dietary: ["Vegetarian", "Vegan"],
    nutritionalInfo: {
      calories: 55,
      protein: "0.8g",
      carbs: "12g",
      fat: "1.5g"
    },
    culturalNote: "Ice paan is a modern summer innovation that provides instant cooling relief while maintaining traditional flavors."
  },
  {
    id: 7,
    name: "Calcutta Paan",
    category: "classic",
    description: "Traditional Kolkata-style paan with special tobacco blend, katha, and authentic Bengali spices.",
    price: 40,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d8b2879-1768329574829.png",
    alt: "Authentic Calcutta paan showing traditional tobacco blend, red katha paste, and Bengali spice mix on fresh betel leaf",
    spiceLevel: "medium",
    sweetnessLevel: "low",
    preparationTime: "3-4 mins",
    availability: "available",
    isCustomerFavorite: true,
    isSeasonal: false,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Tobacco", icon: "FireIcon" },
    { name: "Katha", icon: "SparklesIcon" },
    { name: "Lime", icon: "StarIcon" },
    { name: "Supari", icon: "SparklesIcon" },
    { name: "Cardamom", icon: "StarIcon" }],

    allergens: ["Tobacco", "Nuts"],
    dietary: [],
    nutritionalInfo: {
      calories: 50,
      protein: "0.6g",
      carbs: "9g",
      fat: "1.5g"
    },
    culturalNote: "Calcutta paan represents the rich cultural heritage of Kolkata, where paan shops are social gathering spots."
  },
  {
    id: 8,
    name: "Mango Paan",
    category: "seasonal",
    description: "Seasonal summer special with fresh mango pulp, coconut, and sweet spices. Available only in mango season.",
    price: 55,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed753ccc-1768329572553.png",
    alt: "Vibrant mango paan with visible fresh mango pulp, shredded coconut, and yellow-orange color on green betel leaf",
    spiceLevel: "mild",
    sweetnessLevel: "high",
    preparationTime: "3-4 mins",
    availability: "limited",
    isCustomerFavorite: false,
    isSeasonal: true,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Fresh Mango Pulp", icon: "HeartIcon" },
    { name: "Coconut", icon: "StarIcon" },
    { name: "Gulkand", icon: "HeartIcon" },
    { name: "Fennel Seeds", icon: "StarIcon" },
    { name: "Cardamom", icon: "SparklesIcon" }],

    allergens: ["Coconut"],
    dietary: ["Vegetarian", "Vegan"],
    nutritionalInfo: {
      calories: 75,
      protein: "1.0g",
      carbs: "16g",
      fat: "2.0g"
    },
    culturalNote: "Mango paan celebrates India's favorite fruit during summer season, combining tradition with seasonal freshness."
  },
  {
    id: 9,
    name: "Dry Fruit Paan",
    category: "premium",
    description: "Loaded with premium cashews, almonds, pistachios, and raisins. A nutritious and delicious treat.",
    price: 70,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d366e8a9-1768329574384.png",
    alt: "Premium dry fruit paan packed with visible whole cashews, almonds, pistachios, and golden raisins on betel leaf",
    spiceLevel: "mild",
    sweetnessLevel: "medium",
    preparationTime: "3-4 mins",
    availability: "available",
    isCustomerFavorite: true,
    isSeasonal: false,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Cashews", icon: "StarIcon" },
    { name: "Almonds", icon: "SparklesIcon" },
    { name: "Pistachios", icon: "StarIcon" },
    { name: "Raisins", icon: "HeartIcon" },
    { name: "Gulkand", icon: "HeartIcon" }],

    allergens: ["Nuts"],
    dietary: ["Vegetarian"],
    nutritionalInfo: {
      calories: 110,
      protein: "3.5g",
      carbs: "15g",
      fat: "5.2g"
    },
    culturalNote: "Dry fruit paan combines health benefits of nuts with traditional paan enjoyment, popular among health-conscious customers."
  },
  {
    id: 10,
    name: "Sadha Paan",
    category: "classic",
    description: "Simple traditional paan with basic ingredients. Perfect for purists who love authentic taste.",
    price: 20,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11d8b2879-1768329574829.png",
    alt: "Simple sadha paan showing basic ingredients of betel leaf, lime paste, and areca nut in traditional preparation",
    spiceLevel: "mild",
    sweetnessLevel: "low",
    preparationTime: "1-2 mins",
    availability: "available",
    isCustomerFavorite: false,
    isSeasonal: false,
    ingredients: [
    { name: "Betel Leaf", icon: "SparklesIcon" },
    { name: "Lime", icon: "StarIcon" },
    { name: "Supari", icon: "SparklesIcon" },
    { name: "Katha", icon: "StarIcon" },
    { name: "Cardamom", icon: "SparklesIcon" }],

    allergens: ["Nuts"],
    dietary: ["Vegetarian", "Vegan"],
    nutritionalInfo: {
      calories: 25,
      protein: "0.3g",
      carbs: "5g",
      fat: "0.8g"
    },
    culturalNote: "Sadha paan represents the purest form of traditional paan, unchanged for generations."
  },
  {
    id: 12,
    name: "Kesar Paan",
    category: "premium",
    description: "Luxurious paan infused with premium saffron strands, rose water, and exotic spices. A royal delicacy.",
    price: 90,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19244ae8a-1768329574370.png",
    alt: "Elegant kesar paan with visible golden saffron strands, rose petals, and premium ingredients on pristine betel leaf",
    spiceLevel: "mild",
    sweetnessLevel: "medium",
    preparationTime: "4-5 mins",
    availability: "available",
    isCustomerFavorite: true,
    isSeasonal: false,
    ingredients: [
    { name: "Premium Betel Leaf", icon: "SparklesIcon" },
    { name: "Saffron", icon: "StarIcon" },
    { name: "Rose Water", icon: "HeartIcon" },
    { name: "Gulkand", icon: "HeartIcon" },
    { name: "Pistachios", icon: "StarIcon" },
    { name: "Cardamom", icon: "SparklesIcon" }],

    allergens: ["Nuts"],
    dietary: ["Vegetarian"],
    nutritionalInfo: {
      calories: 80,
      protein: "1.8g",
      carbs: "14g",
      fat: "2.8g"
    },
    culturalNote: "Kesar paan has been a symbol of luxury and celebration in Indian culture, often served at weddings and special occasions."
  }];


  const filteredItems = menuItems.filter((item) => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSpiceLevel = selectedSpiceLevel === 'all' || item.spiceLevel === selectedSpiceLevel;
    const matchesSweetness = selectedSweetness === 'all' || item.sweetnessLevel === selectedSweetness;
    const matchesDietary =
    selectedDietary === 'all' ||
    selectedDietary === 'vegetarian' && item.dietary.includes('Vegetarian') ||
    selectedDietary === 'vegan' && item.dietary.includes('Vegan') ||
    selectedDietary === 'no-tobacco' && !item.allergens.includes('Tobacco');
    const matchesSearch =
    searchQuery === '' ||
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSpiceLevel && matchesSweetness && matchesDietary && matchesSearch;
  });

  const handleResetFilters = () => {
    setSelectedCategory('all');
    setSelectedSpiceLevel('all');
    setSelectedSweetness('all');
    setSelectedDietary('all');
    setSearchQuery('');
  };

  const handleOrderClick = (item: MenuItem) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedItem(null);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:447:6" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="447" data-component-end-line="459" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:448:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="448" data-component-end-line="458" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-8%22%7D" className="container mx-auto px-4 py-8">
          <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:449:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="449" data-component-end-line="457" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-8%22%7D" className="animate-pulse space-y-8">
            <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:450:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="450" data-component-end-line="450" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-surface%20rounded-lg%20w-1%2F3%22%7D" className="h-12 bg-surface rounded-lg w-1/3"></div>
            <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:451:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="451" data-component-end-line="451" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-surface%20rounded-lg%22%7D" className="h-64 bg-surface rounded-lg"></div>
            <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:452:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="452" data-component-end-line="456" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:454:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="454" data-component-end-line="454" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded-lg%22%7D" key={i} className="h-96 bg-surface rounded-lg"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:464:4" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="464" data-component-end-line="586" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:465:6" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="465" data-component-end-line="582" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container%20mx-auto%20px-4%20py-8%20space-y-8%22%7D" className="container mx-auto px-4 py-8 space-y-8">
        {/* Hero Section */}
        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:467:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="467" data-component-end-line="474" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20space-y-4%22%7D" className="text-center space-y-4">
          <h1 data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:468:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="468" data-component-end-line="470" data-component-file="PaanMenuInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-headline%20text-4xl%20lg%3Atext-5xl%20text-primary%22%2C%22textContent%22%3A%22Our%20Paan%20Collection%22%7D" className="font-headline text-4xl lg:text-5xl text-primary">
            Our Paan Collection
          </h1>
          <p data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:471:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="471" data-component-end-line="473" data-component-file="PaanMenuInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-lg%20text-muted-foreground%20max-w-3xl%20mx-auto%22%2C%22textContent%22%3A%22Explore%20our%20authentic%20range%20of%20traditional%20and%20innovative%20paan%20varieties%2C%20crafted%20with%20fresh%20ingredients%20and%20time-honored%20techniques.%20Each%20paan%20is%20a%20celebration%20of%20taste%2C%20culture%2C%20and%20quality.%22%7D" className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
            Explore our authentic range of traditional and innovative paan varieties, crafted with fresh ingredients and time-honored techniques. Each paan is a celebration of taste, culture, and quality.
          </p>
        </div>

        {/* Search Bar */}
        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:477:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="477" data-component-end-line="493" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-2xl%20mx-auto%22%7D" className="max-w-2xl mx-auto">
          <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:478:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="478" data-component-end-line="492" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
            <Icon data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:479:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="479" data-component-end-line="483" data-component-file="PaanMenuInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-4%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
              name="MagnifyingGlassIcon"
              size={20}
              variant="outline"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />

            <input data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:485:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="485" data-component-end-line="490" data-component-file="PaanMenuInteractive.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-12%20pr-4%20py-4%20bg-card%20border%20border-border%20rounded-lg%20font-body%20text-foreground%20placeholder%3Atext-muted-foreground%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%20transition-all%20duration-300%22%7D"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search paan varieties by name or ingredients..."
              className="w-full pl-12 pr-4 py-4 bg-card border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-300" />

          </div>
        </div>

        {/* Filters */}
        <MenuFilters data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:496:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="496" data-component-end-line="505" data-component-file="PaanMenuInteractive.tsx" data-component-name="MenuFilters" data-component-content="%7B%22elementName%22%3A%22MenuFilters%22%7D"
          selectedCategory={selectedCategory}
          selectedSpiceLevel={selectedSpiceLevel}
          selectedSweetness={selectedSweetness}
          selectedDietary={selectedDietary}
          onCategoryChange={setSelectedCategory}
          onSpiceLevelChange={setSelectedSpiceLevel}
          onSweetnessChange={setSelectedSweetness}
          onDietaryChange={setSelectedDietary}
          onReset={handleResetFilters} />


        {/* Results Count */}
        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:509:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="509" data-component-end-line="523" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
          <p data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:510:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="510" data-component-end-line="513" data-component-file="PaanMenuInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-muted-foreground%22%2C%22textContent%22%3A%22Showing%20of%20%20%20varieties%22%7D" className="font-body text-muted-foreground">
            Showing <span data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:511:20" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="511" data-component-end-line="511" data-component-file="PaanMenuInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-foreground%22%7D" className="font-semibold text-foreground">{filteredItems.length}</span> of{' '}
            <span data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:512:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="512" data-component-end-line="512" data-component-file="PaanMenuInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-foreground%22%7D" className="font-semibold text-foreground">{menuItems.length}</span> varieties
          </p>
          {filteredItems.length === 0 &&
          <button data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:515:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="515" data-component-end-line="521" data-component-file="PaanMenuInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-4%20py-2%20text-sm%20font-body%20font-medium%20text-primary%20hover%3Abg-surface%20rounded-lg%20transition-colors%20duration-300%22%7D"
            onClick={handleResetFilters}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-body font-medium text-primary hover:bg-surface rounded-lg transition-colors duration-300">

              <Icon data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:519:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="519" data-component-end-line="519" data-component-file="PaanMenuInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%7D" name="ArrowPathIcon" size={16} variant="outline" />
              <span data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:520:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="520" data-component-end-line="520" data-component-file="PaanMenuInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D">Clear All Filters</span>
            </button>
          }
        </div>

        {/* Menu Grid */}
        {filteredItems.length > 0 ?
        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:527:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="527" data-component-end-line="531" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item) =>
          <MenuCard data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:529:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="529" data-component-end-line="529" data-component-file="PaanMenuInteractive.tsx" data-component-name="MenuCard" data-component-content="%7B%22elementName%22%3A%22MenuCard%22%7D" key={item.id} item={item} onOrderClick={handleOrderClick} />
          )}
          </div> :

        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:533:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="533" data-component-end-line="546" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-16%20space-y-4%22%7D" className="text-center py-16 space-y-4">
            <Icon data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:534:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="534" data-component-end-line="534" data-component-file="PaanMenuInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FaceFrownIcon%22%2C%22className%22%3A%22mx-auto%20text-muted-foreground%22%7D" name="FaceFrownIcon" size={64} variant="outline" className="mx-auto text-muted-foreground" />
            <h3 data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:535:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="535" data-component-end-line="535" data-component-file="PaanMenuInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-headline%20text-2xl%20text-foreground%22%2C%22textContent%22%3A%22No%20Paan%20Varieties%20Found%22%7D" className="font-headline text-2xl text-foreground">No Paan Varieties Found</h3>
            <p data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:536:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="536" data-component-end-line="538" data-component-file="PaanMenuInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-muted-foreground%20max-w-md%20mx-auto%22%2C%22textContent%22%3A%22We%20couldn't%20find%20any%20paan%20varieties%20matching%20your%20filters.%20Try%20adjusting%20your%20search%20criteria%20or%20reset%20all%20filters.%22%7D" className="font-body text-muted-foreground max-w-md mx-auto">
              We couldn't find any paan varieties matching your filters. Try adjusting your search criteria or reset all filters.
            </p>
            <button data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:539:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="539" data-component-end-line="545" data-component-file="PaanMenuInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20space-x-2%20px-6%20py-3%20bg-primary%20text-primary-foreground%20rounded-lg%20font-body%20font-semibold%20hover%3Abg-opacity-90%20transition-all%20duration-300%22%7D"
            onClick={handleResetFilters}
            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-body font-semibold hover:bg-opacity-90 transition-all duration-300">

              <Icon data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:543:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="543" data-component-end-line="543" data-component-file="PaanMenuInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%7D" name="ArrowPathIcon" size={20} variant="outline" />
              <span data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:544:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="544" data-component-end-line="544" data-component-file="PaanMenuInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Reset%20All%20Filters%22%7D">Reset All Filters</span>
            </button>
          </div>
        }

        {/* Cultural Note */}
        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:550:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="550" data-component-end-line="560" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20p-6%20bg-accent%2F10%20border-l-4%20border-accent%20rounded-lg%22%7D" className="max-w-4xl mx-auto p-6 bg-accent/10 border-l-4 border-accent rounded-lg">
          <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:551:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="551" data-component-end-line="559" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-4%22%7D" className="flex items-start space-x-4">
            <Icon data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:552:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="552" data-component-end-line="552" data-component-file="PaanMenuInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22BookOpenIcon%22%2C%22className%22%3A%22text-accent%20flex-shrink-0%20mt-1%22%7D" name="BookOpenIcon" size={24} variant="solid" className="text-accent flex-shrink-0 mt-1" />
            <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:553:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="553" data-component-end-line="558" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
              <h3 data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:554:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="554" data-component-end-line="554" data-component-file="PaanMenuInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-headline%20text-xl%20text-primary%22%2C%22textContent%22%3A%22About%20Paan%20Culture%22%7D" className="font-headline text-xl text-primary">About Paan Culture</h3>
              <p data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:555:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="555" data-component-end-line="557" data-component-file="PaanMenuInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20text-foreground%20leading-relaxed%22%2C%22textContent%22%3A%22Paan%20has%20been%20an%20integral%20part%20of%20Indian%20culture%20for%20over%205%2C000%20years.%20Traditionally%20served%20after%20meals%20as%20a%20digestive%20aid%20and%20mouth%20freshener%2C%20paan%20represents%20hospitality%2C%20celebration%2C%20and%20social%20bonding.%20Each%20region%20of%20India%20has%20developed%20its%20own%20unique%20paan%20varieties%2C%20reflecting%20local%20tastes%20and%20traditions.%20At%20Tawwakal%20Traders%2C%20we%20honor%20this%20rich%20heritage%20while%20maintaining%20the%20highest%20standards%20of%20hygiene%20and%20quality.%22%7D" className="font-body text-sm text-foreground leading-relaxed">
                Paan has been an integral part of Indian culture for over 5,000 years. Traditionally served after meals as a digestive aid and mouth freshener, paan represents hospitality, celebration, and social bonding. Each region of India has developed its own unique paan varieties, reflecting local tastes and traditions. At Tawwakal Traders, we honor this rich heritage while maintaining the highest standards of hygiene and quality.
              </p>
            </div>
          </div>
        </div>

        {/* Bulk Order CTA */}
        <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:563:8" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="563" data-component-end-line="581" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-4xl%20mx-auto%20p-6%20bg-primary%20text-primary-foreground%20rounded-lg%20shadow-organic%22%7D" className="max-w-4xl mx-auto p-6 bg-primary text-primary-foreground rounded-lg shadow-organic">
          <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:564:10" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="564" data-component-end-line="580" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20md%3Aflex-row%20items-center%20justify-between%20space-y-4%20md%3Aspace-y-0%22%7D" className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:565:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="565" data-component-end-line="570" data-component-file="PaanMenuInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20md%3Atext-left%22%7D" className="text-center md:text-left">
              <h3 data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:566:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="566" data-component-end-line="566" data-component-file="PaanMenuInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-headline%20text-2xl%20mb-2%22%2C%22textContent%22%3A%22Need%20Bulk%20Orders%3F%22%7D" className="font-headline text-2xl mb-2">Need Bulk Orders?</h3>
              <p data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:567:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="567" data-component-end-line="569" data-component-file="PaanMenuInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20opacity-90%22%2C%22textContent%22%3A%22Planning%20an%20event%20or%20celebration%3F%20We%20offer%20special%20bulk%20order%20services%20with%20customization%20options.%22%7D" className="font-body text-sm opacity-90">
                Planning an event or celebration? We offer special bulk order services with customization options.
              </p>
            </div>
            <a data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:571:12" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="571" data-component-end-line="579" data-component-file="PaanMenuInteractive.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22href%22%3A%22https%3A%2F%2Fwa.me%2F919876543210%3Ftext%3DHello!%2520I%2520would%2520like%2520to%2520inquire%2520about%2520bulk%2520orders%2520for%2520an%2520event.%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-6%20py-3%20bg-white%20text-primary%20rounded-lg%20font-cta%20font-bold%20hover%3Abg-opacity-90%20shadow-organic%20transition-all%20duration-300%22%7D"
              href="https://wa.me/919876543210?text=Hello!%20I%20would%20like%20to%20inquire%20about%20bulk%20orders%20for%20an%20event."
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 px-6 py-3 bg-white text-primary rounded-lg font-cta font-bold hover:bg-opacity-90 shadow-organic transition-all duration-300">

              <Icon data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:577:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="577" data-component-end-line="577" data-component-file="PaanMenuInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PhoneIcon%22%7D" name="PhoneIcon" size={20} variant="solid" />
              <span data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:578:14" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="578" data-component-end-line="578" data-component-file="PaanMenuInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Contact%20for%20Bulk%20Orders%22%7D">Contact for Bulk Orders</span>
            </a>
          </div>
        </div>
      </div>

      {/* Order Modal */}
      <OrderModal data-component-id="src/app/paan-menu/components/PaanMenuInteractive.tsx:585:6" data-component-path="src/app/paan-menu/components/PaanMenuInteractive.tsx" data-component-line="585" data-component-end-line="585" data-component-file="PaanMenuInteractive.tsx" data-component-name="OrderModal" data-component-content="%7B%22elementName%22%3A%22OrderModal%22%7D" isOpen={isModalOpen} item={selectedItem} onClose={handleCloseModal} />
    </div>);

};

export default PaanMenuInteractive;